import React, { useState, useEffect, useCallback, useRef } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../services/firebase";
import { httpsCallable, getFunctions } from "firebase/functions";
import "./SegmentRegistration.css";
import readyServicesTerms from "./terms/ready-services.json";
import orderServicesTerms from "./terms/order-services.json";
import openMarketTerms from "./terms/open-market.json";
import { useOperator } from "../contexts/OperaotrContext";

// 🔑 Segment Officer mobiles (per segment)
const SEGMENT_OFFICER_MOBILE = {
  gold: "9053999090",
  silver: "9053999090",
  platinum: "9053999090",
  diamond: "9053999090",
};

const SegmentRegistration = () => {
  const { Operator, updateOperator } = useOperator();
  const [segment, setSegment] = useState("gold");
  const [operatorOtp, setOperatorOtp] = useState(["", "", "", "", "", ""]);
  const [segmentOfficerOtp, setSegmentOfficerOtp] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [isOperatorOtpSent, setIsOperatorOtpSent] = useState(false);
  const [isSegmentOfficerOtpSent, setIsSegmentOfficerOtpSent] = useState(false);
  const [operatorOtpId, setOperatorOtpId] = useState(null);
  const [segmentOfficerOtpId, setSegmentOfficerOtpId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState(null);

  const nav = useNavigate();
  const operatorOtpRefs = useRef([]);
  const segmentOfficerOtpRefs = useRef([]);
  const savedId = localStorage.getItem("operatorId");
  const savedMobile = localStorage.getItem("operatorMobile");
  const termsAndConditions = {
    "ready-services": readyServicesTerms,
    "order-services": orderServicesTerms,
    "open-market": openMarketTerms,
  };

  useEffect(() => {
    if (!Operator?.OperatorId && savedId && savedMobile) {
      updateOperator({ OperatorId: savedId, mobile: savedMobile });
    }
  }, [Operator, updateOperator, savedId, savedMobile]);

  useEffect(() => {
    const timer = setTimeout(() => {
      ["ready-services", "order-services", "open-market"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.checked = true;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const openPopup = (serviceType) => {
    setPopupContent(termsAndConditions[serviceType]);
    setShowPopup(true);
    document.body.style.overflow = "hidden";
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupContent(null);
    document.body.style.overflow = "auto";
  };

  // 👤 Send Operator OTP (JMI_SEGMENT_VERIFICATION_OTP - no params)
  const sendOperatorOtp = useCallback(async () => {
    const mobile = Operator?.mobile || localStorage.getItem("operatorMobile");
    if (!mobile) {
      alert("Operator mobile not found.");
      return;
    }

    try {
      const functions = getFunctions(undefined, "us-central1");
      const sendOTP = httpsCallable(functions, "sendOTP");

      const result = await sendOTP({
        mobile,
        useCase: "SEGMENT_VERIFICATION",
      });

      setOperatorOtpId(result.data.otpId);
      setIsOperatorOtpSent(true);
      setOperatorOtp(["", "", "", "", "", ""]);
      alert(`✅ Operator OTP sent to +91${mobile}`);
    } catch (err) {
      console.error("Operator OTP error:", err);
      alert("Failed to send Operator OTP.");
    }
  }, [Operator]);

  // 👮 Send Segment Officer OTP (JMI_SEGMENT_REGISTRATION_OPERATOR_OFFICER_OTP - requires OPERATOR param)
  const sendSegmentOfficerOtp = useCallback(async () => {
    const officerMobile = SEGMENT_OFFICER_MOBILE[segment];
    const operatorId =
      Operator?.OperatorId || localStorage.getItem("operatorId");

    if (!officerMobile) {
      alert("No officer assigned to this segment.");
      return;
    }
    if (!operatorId) {
      alert("Operator ID not found. Please log in again.");
      return;
    }

    try {
      const functions = getFunctions(undefined, "us-central1");
      const sendOTP = httpsCallable(functions, "sendOTP");

      const result = await sendOTP({
        mobile: officerMobile,
        useCase: "SEGMENT_REGISTRATION_OPERATOR_OFFICER",
        params: {
          OPERATOR: operatorId,
        },
      });

      setSegmentOfficerOtpId(result.data.otpId);
      setIsSegmentOfficerOtpSent(true);
      setSegmentOfficerOtp(["", "", "", "", "", ""]);
      alert(`✅ Segment Officer OTP sent to +91${officerMobile}`);
    } catch (err) {
      console.error("Segment Officer OTP error:", err);
      alert("Failed to send Segment Officer OTP.");
    }
  }, [segment, Operator]);

  // 🔍 Verify OTP via Cloud Function
  const verifyOtp = useCallback(async (otpValue, mobile, useCase, otpId) => {
    const fullOtp = otpValue.join("");
    if (fullOtp.length !== 6 || isNaN(fullOtp)) {
      alert("Enter a valid 6-digit OTP.");
      return false;
    }

    try {
      const functions = getFunctions(undefined, "us-central1");
      const verifyOTP = httpsCallable(functions, "verifyOTP");

      const result = await verifyOTP({
        otpId: otpId || `+91${mobile}_${useCase}`,
        otp: fullOtp,
        mobile,
        useCase,
      });

      if (result.data.success) {
        return true;
      }
      alert("Invalid OTP.");
      return false;
    } catch (err) {
      console.error("Verify error:", err);
      if (err.message.includes("expired")) {
        alert("OTP has expired. Please request a new one.");
      } else if (err.message.includes("Invalid")) {
        alert("Invalid OTP.");
      } else {
        alert("Verification failed. Please try again.");
      }
      return false;
    }
  }, []);

  // 💾 Save Registration
  const saveSegmentRegistration = useCallback(async () => {
    const opId = Operator?.OperatorId || localStorage.getItem("operatorId");
    if (!opId) {
      alert("Session expired. Please log in again.");
      return;
    }

    try {
      const regId = opId;
      await setDoc(doc(db, "OperatorSegmentRegistrations", regId), {
        operatorId: opId,
        mobile: savedMobile,
        segment,
        services: {
          readyServices:
            document.getElementById("ready-services")?.checked || false,
          orderServices:
            document.getElementById("order-services")?.checked || false,
          openMarket: document.getElementById("open-market")?.checked || false,
        },
        agreedToTerms: document.getElementById("agree")?.checked,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(
        doc(db, "profile", opId),
        { SegmentRegistration: true },
        { merge: true },
      );

      alert("✅ Segment registration completed!");
      nav("/homepage");
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save.");
    }
  }, [Operator, segment, nav, savedMobile]);

  // 📤 Submit
  const handleSubmit = async () => {
    const services = [
      document.getElementById("ready-services")?.checked,
      document.getElementById("order-services")?.checked,
      document.getElementById("open-market")?.checked,
    ];
    if (!services.some(Boolean)) {
      alert("Select at least one service.");
      return;
    }
    if (!document.getElementById("agree")?.checked) {
      alert("You must agree to the Terms & Conditions.");
      return;
    }
    if (!isOperatorOtpSent) {
      alert("Send Operator OTP first.");
      return;
    }
    if (!isSegmentOfficerOtpSent) {
      alert("Send Segment Officer OTP first.");
      return;
    }

    const operatorMobile =
      Operator?.mobile || localStorage.getItem("operatorMobile");
    const officerMobile = SEGMENT_OFFICER_MOBILE[segment];

    // Map aliases to canonical template names
    const operatorUseCase = "SEGMENT_VERIFICATION"; // JMI_SEGMENT_VERIFICATION_OTP
    const officerUseCase = "SEGMENT_REGISTRATION_OPERATOR_OFFICER"; // JMI_SEGMENT_REGISTRATION_OPERATOR_OFFICER_OTP

    const operatorOk = await verifyOtp(
      operatorOtp,
      operatorMobile,
      operatorUseCase,
      operatorOtpId,
    );
    if (!operatorOk) return;

    const officerOk = await verifyOtp(
      segmentOfficerOtp,
      officerMobile,
      officerUseCase,
      segmentOfficerOtpId,
    );
    if (!officerOk) return;

    await saveSegmentRegistration();
    nav("/homepage");
  };

  // ======================
  // OTP Input Component
  // ======================
  const OtpField = ({ label, otp, type, isSent, onSend }) => {
    const refs = type === "operator" ? operatorOtpRefs : segmentOfficerOtpRefs;

    const handleOtpChange = (index, value) => {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (type === "operator") {
        const newOtp = [...operatorOtp];
        newOtp[index] = numericValue;
        setOperatorOtp(newOtp);
      } else {
        const newOtp = [...segmentOfficerOtp];
        newOtp[index] = numericValue;
        setSegmentOfficerOtp(newOtp);
      }
      // Auto-focus next field on input - use setTimeout to ensure ref is set
      if (numericValue && index < 5) {
        setTimeout(() => refs.current[index + 1]?.focus(), 0);
      }
    };

    const handleKeyDown = (index, e) => {
      if (e.key === "Backspace") {
        if (!otp[index] && index > 0) {
          refs.current[index - 1]?.focus();
        } else if (otp[index]) {
          handleOtpChange(index, "");
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        refs.current[index - 1]?.focus();
      } else if (e.key === "ArrowRight" && index < 5) {
        refs.current[index + 1]?.focus();
      } else if (e.key === "Enter" && index < 5) {
        refs.current[index + 1]?.focus();
      }
    };

    const handlePaste = (e) => {
      e.preventDefault();
      const pasteData = e.clipboardData.getData("text");
      const numbers = pasteData.replace(/[^0-9]/g, "").slice(0, 6);
      if (numbers.length > 0) {
        if (type === "operator") {
          const newOtp = [...operatorOtp];
          numbers.split("").forEach((num, i) => {
            if (i < 6) newOtp[i] = num;
          });
          setOperatorOtp(newOtp);
        } else {
          const newOtp = [...segmentOfficerOtp];
          numbers.split("").forEach((num, i) => {
            if (i < 6) newOtp[i] = num;
          });
          setSegmentOfficerOtp(newOtp);
        }
        const lastIndex = Math.min(numbers.length - 1, 5);
        setTimeout(() => refs.current[lastIndex]?.focus(), 0);
      }
    };

    return (
      <div
        className="form-group"
        style={{
          border: "1px solid #ddd",
          padding: "15px",
          borderRadius: "8px",
        }}
      >
        <label>{label} *</label>
        <div className="otp-inputs">
          {otp.map((d, i) => (
            <input
              key={i}
              type="text"
              maxLength="1"
              value={d}
              onChange={(e) => handleOtpChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              ref={(el) => (refs.current[i] = el)}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              style={{
                width: "40px",
                height: "40px",
                textAlign: "center",
                fontSize: "18px",
                margin: "0 5px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          ))}
        </div>
        <span
          onClick={onSend}
          className="resend-otp"
          style={{
            cursor: "pointer",
            color: isSent ? "green" : "#007bff",
            display: "block",
            marginTop: "8px",
            fontSize: "14px",
          }}
        >
          {isSent ? "Resend OTP" : `Send ${label}`}
        </span>
      </div>
    );
  };

  return (
    <div className="segment-registration-container">
      <header className="sticky-header">
        <h1>Segment Registration</h1>
      </header>

      <main className="main-content">
        <section className="segment-information">
          <h2>Segment Information</h2>
          <div className="form-group">
            <label>Segment *</label>
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
            >
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="platinum">Platinum</option>
              <option value="diamond">Diamond</option>
            </select>
          </div>
        </section>

        <section className="service-selection">
          <h2>Service Selection *</h2>
          <div className="checkbox-group">
            <input type="checkbox" id="ready-services" />
            <label htmlFor="ready-services">Ready Services</label>
            <button
              type="button"
              className="terms-link"
              onClick={() => openPopup("ready-services")}
            >
              Terms & Conditions
            </button>
          </div>
          <div className="checkbox-group">
            <input type="checkbox" id="order-services" />
            <label htmlFor="order-services">Order Services</label>
            <button
              type="button"
              className="terms-link"
              onClick={() => openPopup("order-services")}
            >
              Terms & Conditions
            </button>
          </div>
          <div className="checkbox-group">
            <input type="checkbox" id="open-market" />
            <label htmlFor="open-market">Open Market</label>
            <button
              type="button"
              className="terms-link"
              onClick={() => openPopup("open-market")}
            >
              Terms & Conditions
            </button>
          </div>
          <div className="checkbox-group agreement">
            <input type="checkbox" id="agree" required />
            <label htmlFor="agree">
              I agree to all availing services with Terms & Conditions *
            </label>
          </div>
        </section>

        <section className="verification">
          <h2>Verification *</h2>
          <OtpField
            label="Operator OTP"
            otp={operatorOtp}
            type="operator"
            isSent={isOperatorOtpSent}
            onSend={sendOperatorOtp}
          />
          <OtpField
            label="Segment Officer OTP"
            otp={segmentOfficerOtp}
            type="officer"
            isSent={isSegmentOfficerOtpSent}
            onSend={sendSegmentOfficerOtp}
          />
        </section>
      </main>

      <footer className="sticky-footer">
        <button className="draft-btn">Save Draft</button>
        <button className="submit-btn" onClick={handleSubmit}>
          Submit
        </button>
      </footer>

      {showPopup && popupContent && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>{popupContent.title}</h3>
              <button className="close-btn" onClick={closePopup}>
                ×
              </button>
            </div>
            <div className="popup-body">
              {popupContent.content.map((sec, i) => (
                <div key={i} className="terms-section">
                  <h4>{sec.section}</h4>
                  <p>{sec.text}</p>
                </div>
              ))}
            </div>
            <div className="popup-footer">
              <button className="popup-close-btn" onClick={closePopup}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentRegistration;
